import { z } from 'zod';
import { Email, Password, Timezone, User } from './user.js';
/**
 * Dashboard login (knowledge doc sections 15 and 17).
 *
 * Section 20 chose new authentication built to the three roles rather than reusing
 * another project's, because none of them carries the role ladder plus the
 * re-check that high-risk actions need.
 *
 * The session itself never appears in a payload. It travels as an opaque random
 * token in an `HttpOnly` cookie, so no script in the dashboard — or injected into
 * it — can read it, and the API stores only the token's hash.
 */
/** The cookie the browser carries. Named so it is obvious in a devtools listing. */
export const SESSION_COOKIE_NAME = 'bz_session';
/**
 * Silence ends a session after four hours, and no session outlives a day whatever
 * happens. The absolute bound is what limits a stolen cookie: idle expiry alone
 * keeps renewing for as long as the thief keeps using it.
 */
export const SESSION_IDLE_TIMEOUT_SECONDS = 4 * 60 * 60;
export const SESSION_ABSOLUTE_TIMEOUT_SECONDS = 24 * 60 * 60;
/**
 * Six digits from an authenticator app. Spaces are stripped because apps display
 * the code in two groups of three and people type it as they see it.
 */
export const TOTP_CODE_DIGITS = 6;
export const TotpCode = z
    .string()
    .transform((value) => value.replace(/\s/g, ''))
    .pipe(z.string().regex(/^\d{6}$/, 'six digits'));
/**
 * `totpCode` is absent on the first attempt. When the account has an authenticator
 * in force the API answers `totp_required`, and the dashboard asks for the code and
 * sends the same request again — no intermediate token, because there is nothing
 * for one to protect that the password does not already.
 */
export const LoginRequest = z.object({
    email: Email,
    password: Password,
    totpCode: TotpCode.optional(),
});
/**
 * What the dashboard needs to draw itself: who is signed in, and which time zone to
 * render instants in. `instanceTimezone` is the installation's default, used when
 * the user has not chosen one (section 20).
 */
export const SessionInfo = z.object({
    user: User,
    instanceTimezone: Timezone,
    /**
     * When the password was last re-entered, or null. The dashboard uses it to decide
     * whether to prompt *before* sending a high-risk request rather than after the
     * API refuses one.
     */
    reauthenticatedAt: z.iso.datetime({ offset: true }).nullable(),
});
export const LoginResponse = SessionInfo;
/**
 * The one-time link `install.sh central` prints (section 9; P8.1) carries a token
 * after `#`, so it never travels in a request line or a server's log: 32 random
 * bytes, base64url. Spending it creates the installation's first account, an
 * admin, and signs that person in.
 */
export const SETUP_TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/;
export const SetupRequest = z.object({
    token: z.string().regex(SETUP_TOKEN_PATTERN),
    email: Email,
    password: Password,
    timezone: Timezone.optional(),
});
/**
 * Section 17's gate for high-risk actions: "re-entering the password **or** a 2FA
 * code". Exactly one of the two, so a caller cannot send an empty body and cannot
 * send both and leave the server to decide which mattered.
 */
export const ReauthRequest = z
    .object({
    password: Password.optional(),
    totpCode: TotpCode.optional(),
})
    .refine((value) => (value.password === undefined) !== (value.totpCode === undefined), 'send either a password or an authenticator code, not both');
export const ReauthMethod = z.enum(['password', 'totp']);
export const ReauthResponse = z.object({
    reauthenticatedAt: z.iso.datetime({ offset: true }),
    /** Seconds until the re-check goes stale, so the dashboard can show a countdown. */
    expiresInSeconds: z.number().int().positive(),
    method: ReauthMethod,
});
/**
 * Enrolling an authenticator, in two steps.
 *
 * The secret is issued first and only comes into force once a code proves the app
 * holds it. Enabling on the secret alone would lock out anyone whose scan silently
 * failed — with a fleet console, that is an admin locked out of their own fleet.
 *
 * Starting enrollment needs a recent password re-check, because someone who walks
 * up to an unattended session must not be able to bind their own phone to it.
 */
export const TotpSetupResponse = z.object({
    /** Shown once, for typing into an app that cannot scan. */
    secret: z.string().min(16).max(64),
    /** The `otpauth://` URI behind the QR code. */
    uri: z.string().startsWith('otpauth://totp/'),
});
export const TotpEnableRequest = z.object({ totpCode: TotpCode });
/** Turning it off is a change to a credential, so it costs a code of its own. */
export const TotpDisableRequest = z.object({ totpCode: TotpCode });
/**
 * Changing a password ends every other session for that person: a password is
 * changed because it might be known, and leaving the old sessions open would make
 * the change cosmetic.
 */
export const ChangePasswordRequest = z.object({
    currentPassword: Password,
    newPassword: Password,
});
export const ChangePasswordResponse = z.object({
    /** How many other sessions were ended, so the UI can say what just happened. */
    otherSessionsEnded: z.number().int().nonnegative(),
});
/**
 * Section 20: the display time zone is a user preference with an instance default.
 * `null` clears the choice and goes back to the installation's.
 *
 * It is the only preference so far, and deliberately a separate schema from `User`:
 * a person may change their own time zone, and nothing else about their row.
 */
export const UpdatePreferencesRequest = z.object({
    timezone: Timezone.nullable(),
});
/**
 * Failure codes the dashboard branches on. Kept here because both sides need the
 * same list: the API raises them and React decides what to show.
 *
 * `invalid_credentials` covers an unknown address and a wrong password alike — the
 * two must not be distinguishable, or the login form becomes a way to find out
 * which addresses have accounts.
 */
export const AuthErrorCode = z.enum([
    'invalid_credentials',
    'account_disabled',
    'unauthenticated',
    'insufficient_role',
    'reauth_required',
    'bad_origin',
    /** The password was right; the account also needs an authenticator code. */
    'totp_required',
    /** A code was sent and did not match, or had already been used. */
    'totp_invalid',
    /** The installation has no `SECRET_ENCRYPTION_KEY`, so no secret can be stored. */
    'encryption_unavailable',
]);
//# sourceMappingURL=auth.js.map